import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
// form
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  useIntakeFormStore,
  caregiverInfoSchema,
  TCaregiverInfoSchema,
  SUPPORTED_RELATIONSHIP_TO_CHILD_VALUES,
} from "../intakeFormState";
// UI components
import { HookFormPhoneNumberInput } from "../../../components/PhoneNumberInput/PhoneNumberInput";
import { TextInput } from "../../../components/TextInput/TextInput";
import { IntakeFormScreenContainer } from "../IntakeFormScreenContainer";
import { CheckboxInput } from "../../../components/CheckboxInput/CheckboxInput";
import { RadioSelectInput } from "../../../components/RadioSelectInput/RadioSelectInput";
import { NextButton } from "../NextButton";
// controller (from layout)
import { useOutletContext } from "react-router-dom";
import type { IntakeOutletContext } from "../IntakeFormLayout";
// utils

export const IntakeFormCaregiverInfoScreen = React.memo(() => {
  const { caregiverInfo, handleCaregiverInfoSubmit } = useIntakeFormStore();
  const { t } = useTranslation();

  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
  } = useForm<TCaregiverInfoSchema>({
    resolver: yupResolver(caregiverInfoSchema),
    mode: "onBlur",
    progressive: true,
    reValidateMode: "onBlur",
    defaultValues: {
      ...caregiverInfo,
      // upon page refresh, prefer value saved in store but if not set, default to checked
      emailMarketingOptIn: caregiverInfo?.emailMarketingOptIn ?? true,
    },
  });

  const outletContext: IntakeOutletContext = useOutletContext();
  const { onSubmit: afterSubmit } = outletContext;

  const onSubmit = useCallback(
    (data: TCaregiverInfoSchema) => {
      // TODO: NEED TO CHECK CONSENT STATUS AND FLASH A TOAST IF THEY DON'T CONSENT
      handleCaregiverInfoSubmit(data);
      afterSubmit();
    },
    [handleCaregiverInfoSubmit, afterSubmit],
  );

  return (
    <IntakeFormScreenContainer title={t("intakeCaregiverInfo.enterInformation")}>
      <form className="min-w-full" onSubmit={handleSubmit(onSubmit)}>
        <TextInput
          label={t("intakeCaregiverInfo.firstName")}
          isRequired
          {...register("firstName")}
          hookFormFieldError={errors.firstName}
        />

        <div className="mt-1">
          <TextInput
            label={t("intakeCaregiverInfo.lastName")}
            isRequired
            {...register("lastName")}
            hookFormFieldError={errors.lastName}
          />
        </div>

        <div className="mt-1">
          <TextInput
            label={t("intakeCaregiverInfo.email")}
            isRequired
            {...register("email")}
            hookFormFieldError={errors.email}
          />
        </div>

        <CheckboxInput
          isRequired={false}
          label={t("intakeCaregiverInfo.emailMarketingOptIn")}
          {...register("emailMarketingOptIn")}
        />

        <div className="mt-8">
          <HookFormPhoneNumberInput
            label={t("intakeCaregiverInfo.phoneNumber")}
            isRequired
            control={control}
            hookFormFieldError={errors.phoneNumber}
          />
        </div>

        <CheckboxInput
          isRequired={false}
          label={t("intakeCaregiverInfo.smsMarketingOptIn")}
          {...register("smsMarketingOptIn")}
        />

        <div className="mt-8 flex flex-col">
          <RadioSelectInput
            {...register("relationshipToChild")}
            label={t("intakeCaregiverInfo.relationshipToChild")}
            isRequired
            // TODO: can we ensure type safety here? i.e. `choices[number].value` type must match validation schema?
            choices={SUPPORTED_RELATIONSHIP_TO_CHILD_VALUES.map((v) => {
              // NOTE: this only works in TS bc `as const` is used on the array - otherwise, it errors out
              return { label: t(`intakeCaregiverInfo.relationshipToChildChoices.${v}`), value: v };
            })}
            hookFormFieldError={errors.relationshipToChild}
          />
        </div>

        <NextButton />
      </form>
    </IntakeFormScreenContainer>
  );
});

IntakeFormCaregiverInfoScreen.displayName = "IntakeFormCaregiverInfoScreen";
